// Toasts stack at the bottom (newest nearest the edge), up to three at once, each on a gentle spring. One with an
// action (Undo, Open) stays 8 seconds, others 3.2; hovering or focusing the stack pauses them all.
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { duration, ease, spring } from '../lib/motion';

export interface ToastAction { label: string; run: () => void }
type Show = (msg: string, action?: ToastAction) => void;
const Ctx = createContext<Show>(() => undefined);
export const useToast = () => useContext(Ctx);

interface Item { id: number; text: string; action?: ToastAction | undefined; ms: number }
const MAX = 3;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Item[]>([]);
  const [paused, setPaused] = useState(false);
  const next = useRef(1);
  const show = useCallback<Show>((text, action) => {
    setItems(cur => [...cur, { id: next.current++, text, action, ms: action ? 8000 : 3200 }].slice(-MAX));
  }, []);
  const remove = useCallback((id: number) => setItems(cur => cur.filter(t => t.id !== id)), []);
  return (
    <Ctx.Provider value={show}>
      {children}
      <div className="toasts" role="region" aria-label="Messages" aria-live="polite"
        onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}>
        <AnimatePresence initial={false}>
          {items.map(t => <Toast key={t.id} t={t} paused={paused} onDone={remove} />)}
        </AnimatePresence>
      </div>
    </Ctx.Provider>
  );
}

function Toast({ t, paused, onDone }: { t: Item; paused: boolean; onDone: (id: number) => void }) {
  // Time left survives pauses.
  const left = useRef(t.ms);
  useEffect(() => {
    if (paused) return;
    const start = Date.now();
    const h = window.setTimeout(() => onDone(t.id), left.current);
    return () => { window.clearTimeout(h); left.current -= Date.now() - start; };
  }, [paused, t.id, onDone]);
  return (
    <motion.div layout className="toast3" role="status"
      initial={{ opacity: 0, y: 16, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1, transition: spring.gentle }}
      exit={{ opacity: 0, scale: 0.96, transition: { duration: duration.fast, ease } }}>
      <span>{t.text}</span>
      {t.action && <button type="button" className="tlink" onClick={() => { t.action!.run(); onDone(t.id); }}>{t.action.label}</button>}
    </motion.div>
  );
}
